import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * Mobile (<lg): bottom-sheet sliding up.
 * Desktop (≥lg): right-side drawer, full height, fixed-width column.
 *
 * `header` lets a caller render its own header (e.g. the stepper wizard).
 * `wide` switches the desktop drawer from 520px → 720px for big forms.
 */
type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  snapPoint?: "half" | "full";
  wide?: boolean;
};

export function BottomSheet({
  open,
  onClose,
  title,
  description,
  header,
  children,
  footer,
  snapPoint = "half",
  wide = false,
}: Props) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="motion-overlay fixed inset-0 z-[100] bg-black/30 backdrop-blur-[1px]" />
        <Dialog.Content
          className={cn(
            "motion-sheet fixed z-[101] bg-surface shadow-lg outline-none flex flex-col",
            // Mobile: slide from bottom
            "left-0 right-0 bottom-0 rounded-t-2xl",
            snapPoint === "full" ? "max-h-[95vh]" : "max-h-[80vh]",
            // Desktop: right-side drawer, full height
            "lg:bottom-0 lg:top-0 lg:left-auto lg:right-0 lg:rounded-none lg:rounded-l-2xl lg:max-h-none lg:h-full",
            wide ? "lg:w-[720px]" : "lg:w-[520px]",
          )}
        >
          {/* Mobile drag handle */}
          <div className="lg:hidden flex justify-center pt-2 shrink-0">
            <span className="block h-1 w-9 rounded-full bg-[var(--color-border-strong)]" />
          </div>

          {header ? (
            <>
              <Dialog.Title className="sr-only">{title}</Dialog.Title>
              <div className="shrink-0">{header}</div>
            </>
          ) : (
            <div className="flex items-start justify-between px-5 pt-3 pb-3 lg:pt-5 shrink-0">
              <div>
                <Dialog.Title className="text-[17px] font-semibold text-[var(--color-text)]">
                  {title}
                </Dialog.Title>
                {description ? (
                  <Dialog.Description className="text-[13px] text-[var(--color-text-muted)] mt-1">
                    {description}
                  </Dialog.Description>
                ) : null}
              </div>
              <Dialog.Close
                className="rounded-md p-2 -m-2 hover:bg-[var(--color-surface-hover)]"
                aria-label="Close"
              >
                <X size={18} />
              </Dialog.Close>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-5 pb-4">{children}</div>
          {footer ? (
            <div className="border-t border-[var(--color-border)] px-5 py-3 pb-safe bg-surface shrink-0">
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
